/**
 * What a screen tells DataTable about its columns, and the layout a person
 * makes of them.
 *
 * A column is an id, the header word in the account's language, the width
 * the table reserves for it, and the cell. The layout is the order and the
 * visibility of those ids, remembered per table id in localStorage under
 * `taxi-app.<tableId>.columns`, the key the board used before there was a
 * shared table, so a dispatcher's saved layout survives the port.
 */
import type {ReactNode} from 'react'

export interface DataColumn<Row> {
  id: string
  /** The header word, already in the account's language. */
  label: string
  /** Pixels. The table's minimum width is the sum of the visible columns. */
  width: number
  cell: (row: Row) => ReactNode
  /** Shown before anybody hides it. Default true. */
  defaultVisible?: boolean
  /** Numbers sit on the right. */
  align?: 'start' | 'end'
  /**
   * The cell's words as plain text, for the cell's `title`: a cell is clipped
   * at its column's edge (design-consistency.md, rule 8), and the title says
   * what the ellipsis hides. A cell whose `cell` returns a string carries it
   * without this.
   */
  text?: (row: Row) => string | null | undefined
  /**
   * The cell holds buttons: they never wrap onto a second line, and the
   * column's width is the width its buttons take, the buttons are never
   * clipped.
   */
  controls?: boolean
}

export interface ColumnLayout {
  id: string
  visible: boolean
}

export const defaultLayout = <Row>(
  columns: DataColumn<Row>[]
): ColumnLayout[] =>
  columns.map(c => ({id: c.id, visible: c.defaultVisible ?? true}))

const storageKey = (tableId: string) => `taxi-app.${tableId}.columns`

/**
 * The saved layout, or the default. Storage may be missing or throw, then the
 * default. A column added since the layout was saved is appended with its
 * default, one that no longer exists is dropped.
 */
export const loadLayout = <Row>(
  tableId: string,
  columns: DataColumn<Row>[]
): ColumnLayout[] => {
  const defaults = defaultLayout(columns)
  try {
    const raw = window.localStorage.getItem(storageKey(tableId))
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as ColumnLayout[]
    if (!Array.isArray(parsed)) return defaults
    const known = new Set(defaults.map(c => c.id))
    const kept = parsed
      .filter(c => c && typeof c.id === 'string' && known.has(c.id))
      .map(c => ({id: c.id, visible: c.visible !== false}))
    defaults.forEach(c => {
      if (!kept.some(k => k.id === c.id)) kept.push(c)
    })
    return kept
  } catch {
    return defaults
  }
}

export const saveLayout = (tableId: string, layout: ColumnLayout[]) => {
  try {
    window.localStorage.setItem(storageKey(tableId), JSON.stringify(layout))
  } catch {
    /* a browser that blocks storage keeps the layout for the tab only */
  }
}

/**
 * The page size beside the layout (design-consistency.md, rule 10): the four
 * sizes are the reader's choice, remembered per table under
 * `taxi-app.<tableId>.pageSize`, and a table nobody has chosen for keeps the
 * size its screen has always had.
 */
export const PAGE_SIZES = [10, 25, 50, 100] as const

const pageSizeKey = (tableId: string) => `taxi-app.${tableId}.pageSize`

/** The saved size, or the screen's default. A size that is not one of the four is ignored. */
export const loadPageSize = (tableId: string, fallback: number): number => {
  try {
    const raw = window.localStorage.getItem(pageSizeKey(tableId))
    if (!raw) return fallback
    const size = Number.parseInt(raw, 10)
    return (PAGE_SIZES as readonly number[]).includes(size) ? size : fallback
  } catch {
    return fallback
  }
}

export const savePageSize = (tableId: string, size: number) => {
  try {
    window.localStorage.setItem(pageSizeKey(tableId), String(size))
  } catch {
    /* a browser that blocks storage keeps the size for the tab only */
  }
}
